import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'

import type { LotOption } from '@/components/lots/lot-items-field'
import type { AgriMaterialStockOutNote } from '@/lib/api/agri-material-lot-api'
import type { AgriOutSubmit } from '@/components/agricultural-materials/agri-out-note-form'
import type {
    AgriOutNoteFormReason,
    AgriOutNoteFormValues,
} from '@/lib/schemas/agri-note-schemas'
import { AgriOutNoteForm } from '@/components/agricultural-materials/agri-out-note-form'
import { useAuth } from '@/hooks/use-auth'
import {
    useAgriOutNote,
    useUpdateAgriOutNote,
} from '@/hooks/use-agri-material-lot-query'
import { m } from '@/lib/paraglide/messages'
import { isAgriOutNoteFormReason } from '@/lib/schemas/agri-note-schemas'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agricultural-materials/stock-out-notes/$id_/edit',
)({
    component: EditAgriOutNotePage,
})

export function EditAgriOutNotePage() {
    const { id } = Route.useParams()
    const { user, hasPermission } = useAuth()
    const { data: res, isLoading } = useAgriOutNote(id)
    const note = res?.data

    if (!hasPermission('agri_material_lots.write')) {
        return (
            <div className="p-6 text-sm text-muted-foreground">
                {m.aml_no_permission_create()}
            </div>
        )
    }
    if (isLoading) {
        return (
            <div className="p-6 text-muted-foreground">{m.aml_loading()}</div>
        )
    }
    if (!note) {
        return (
            <div className="p-6 text-muted-foreground">
                {m.aml_note_not_found()}
            </div>
        )
    }
    const editableReason = isAgriOutNoteFormReason(note.reason)
        ? note.reason
        : null
    const isCreator = !!user?.id && note.created_by_id === user.id
    const editable =
        note.status === 'pending' && isCreator && editableReason !== null

    if (!editable || editableReason === null) {
        return (
            <div className="p-6 text-muted-foreground">
                {m.pl_pnk_edit_not_allowed()}
            </div>
        )
    }
    return <EditForm note={note} reason={editableReason} />
}

function EditForm({
    note,
    reason,
}: {
    note: AgriMaterialStockOutNote
    reason: AgriOutNoteFormReason
}) {
    const navigate = useNavigate()
    const update = useUpdateAgriOutNote()

    const back = () =>
        navigate({
            to: '/agricultural-materials/stock-out-notes/$id',
            params: { id: note.id },
        })

    const initialValues: AgriOutNoteFormValues = {
        reason,
        notes: note.notes ?? '',
        items: (note.items ?? []).map((it) => ({
            kind: 'existing' as const,
            agri_material_lot_id: it.agri_material_lot_id,
            grower_id: it.grower_id ?? undefined,
            grower_name: it.grower_name,
            quantity: it.quantity,
        })),
    }
    // Nhãn lô của phiếu — bảo đảm trigger hiển thị đúng dù lô nằm ngoài trang
    // search hiện tại của picker.
    const seedLotOptions: LotOption[] = (note.items ?? []).map((it) => ({
        value: it.agri_material_lot_id,
        label: `${it.lot_code ? `${it.lot_code} — ` : ''}${it.material_name ?? ''}`,
    }))

    const handleSubmit = async (
        value: AgriOutSubmit,
        workflow: Parameters<
            React.ComponentProps<typeof AgriOutNoteForm>['onSubmit']
        >[1],
    ) => {
        try {
            await workflow.run(
                () => update.mutateAsync({ id: note.id, req: value }),
                (res) => res.data?.id,
            )
            await back()
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_new_error_create(),
            )
        }
    }

    return (
        <AgriOutNoteForm
            mode="edit"
            initialValues={initialValues}
            seedLotOptions={seedLotOptions}
            isPending={update.isPending}
            onSubmit={handleSubmit}
            onCancel={back}
        />
    )
}
